/**
 * StructuredDataCard — Renders structured_data payload from Maestro responses
 * MAESTRO:P0D
 *
 * Handles three known data shapes:
 *  - type: "comic"      → comic details (name, issue, grade, price)
 *  - type: "price_data" → market price table
 *  - type: "vault"      → vault item list with values
 *
 * Falls back to a generic key/value table for unknown types.
 */

function ComicCard({ data }) {
  const fields = [
    ['Title', data.title || data.name],
    ['Issue', data.issue_number != null ? `#${data.issue_number}` : null],
    ['Publisher', data.publisher],
    ['Year', data.year],
    ['Grade', data.grade],
    ['Price', data.price != null ? `$${Number(data.price).toFixed(2)}` : null],
    ['Condition', data.condition],
  ].filter(([, v]) => v != null);

  return (
    <div
      className="mt-2 bg-zinc-900 border border-orange-500/30 rounded-lg p-3 text-xs font-mono"
      aria-label="Comic information card"
    >
      <p className="text-orange-400 font-semibold mb-2 uppercase tracking-wider text-[10px]">Comic</p>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
        {fields.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-zinc-500">{label}</dt>
            <dd className="text-zinc-200 truncate">{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function PriceDataCard({ data }) {
  const rows = data.rows || data.prices || [];

  return (
    <div
      className="mt-2 bg-zinc-900 border border-orange-500/30 rounded-lg p-3 text-xs font-mono overflow-x-auto"
      aria-label="Price data table"
    >
      <p className="text-orange-400 font-semibold mb-2 uppercase tracking-wider text-[10px]">
        {data.title || 'Price Data'}
      </p>
      {rows.length > 0 ? (
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-zinc-800">
              {Object.keys(rows[0]).map(k => (
                <th key={k} className="pb-1 pr-4 text-zinc-500 font-normal capitalize">{k.replace(/_/g, ' ')}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className="border-b border-zinc-800/50 last:border-0">
                {Object.values(row).map((v, j) => (
                  <td key={j} className="py-1 pr-4 text-zinc-200">{String(v ?? '—')}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="text-zinc-500">No price data available.</p>
      )}
    </div>
  );
}

function VaultCard({ data }) {
  const items = data.items || [];

  return (
    <div
      className="mt-2 bg-zinc-900 border border-orange-500/30 rounded-lg p-3 text-xs font-mono"
      aria-label="Vault items list"
    >
      <p className="text-orange-400 font-semibold mb-2 uppercase tracking-wider text-[10px]">
        {data.title || 'Vault Items'}
        {data.total_value != null && (
          <span className="ml-2 text-zinc-400 normal-case">
            Total: ${Number(data.total_value).toFixed(2)}
          </span>
        )}
      </p>
      {items.length > 0 ? (
        <ul className="space-y-1">
          {items.map((item, i) => (
            <li key={i} className="flex justify-between items-center py-0.5 border-b border-zinc-800/50 last:border-0">
              <span className="text-zinc-200 truncate mr-4">{item.name || item.title || `Item ${i + 1}`}</span>
              {item.value != null && (
                <span className="text-orange-400 flex-shrink-0">${Number(item.value).toFixed(2)}</span>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-zinc-500">No items found.</p>
      )}
    </div>
  );
}

function GenericCard({ data }) {
  const entries = Object.entries(data).filter(([k]) => k !== 'type');

  return (
    <div
      className="mt-2 bg-zinc-900 border border-zinc-700 rounded-lg p-3 text-xs font-mono"
      aria-label="Data card"
    >
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
        {entries.map(([key, value]) => (
          <div key={key} className="contents">
            <dt className="text-zinc-500 capitalize">{key.replace(/_/g, ' ')}</dt>
            <dd className="text-zinc-200 truncate">{String(value ?? '—')}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

// P1 card imports — relocated to vault/ and predictions/ in MAESTRO:P8B
import OwnershipCard from '../vault/OwnershipCard';
import ValuationCard from '../vault/ValuationCard';
import PortfolioImpactCard from '../predictions/PortfolioImpactCard';
import ConventionOpportunityCard from '../vault/ConventionOpportunityCard';

/**
 * @param {Object} props
 * @param {Object} props.data - structured_data payload from the API response
 */
export default function StructuredDataCard({ data }) {
  if (!data || typeof data !== 'object') return null;

  const type = data.type;

  // P0 card types
  if (type === 'comic') return <ComicCard data={data} />;
  if (type === 'price_data') return <PriceDataCard data={data} />;
  if (type === 'vault') return <VaultCard data={data} />;

  // P1 card types
  if (type === 'ownership') return <OwnershipCard data={data} />;
  if (type === 'valuation') return <ValuationCard data={data} />;
  if (type === 'portfolio_impact') return <PortfolioImpactCard data={data} />;
  if (type === 'convention_opportunity') return <ConventionOpportunityCard data={data} />;

  return <GenericCard data={data} />;
}
